import { html, css } from 'lit';
import KaiElement from '../base';

const types = {
  DETERMINED: 'determined',
  INDETERMINATE: 'indeterminate',
  BUFFERED: 'buffered',
  ONTOP: 'ontop',
};

class ProgressElement extends KaiElement {
  static get is() {
    return 'kai-progress';
  }

  static get properties() {
    return {
      percent: { type: Number },
      bufferedpercent: { type: Number },
      type: { type: String },
      label: { type: String },
    };
  }

  static get styles() {
    return [
      super.styles,
      /* eslint-disable */
      css`
        :host {
          width: 100%;
        }

        .label {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          color: var(--color-gs60);
        }

        .progress {
          width: 100%;
          height: 0.4rem;
        }

        .space {
          padding: 0.6rem 0;
        }

        .wrapper {
          width: 100%;
          height: 0.4rem;
          border-radius: 0.5rem;
          overflow: hidden;
          position: relative;
        }

        .ontop-track,
        .track {
          width: 100%;
          height: 0.2rem;
          margin: 0.1rem 0;
          border-radius: 0.2rem;
          background-color: var(--color-gs20);
        }

        .ontop-track {
          background-color: transparent;
        }

        .indicator,
        .status {
          position: relative;
          top: -0.4rem;
          left: 0;
          right: unset;
          height: 0.4rem;
          border-radius: 0.5rem;
          background-color: var(--theme-color);
          transform: translateX(-100%);
          transition: transform 0.05s cubic-bezier(0.65, 0.815, 0.735, 0.395);
        }

        .status {
          top: -0.8rem;
        }

        .indicator:-moz-dir(rtl),
        .status:-moz-dir(rtl) {
          left: unset;
          right: 0;
        }

        .wrapper.ontop,
        .ontop {
          border-radius: 0 0.5rem 0.5rem 0;
        }

        .wrapper.ontop:-moz-dir(rtl),
        .ontop:-moz-dir(rtl) {
          border-radius: 0.5rem 0 0 0.5rem;
        }

        .wrapper.ontop.complete,
        .ontop.complete {
          border-radius: 0;
        }

        .buffered {
          background-color: var(--color-gs80);
        }

        .indeterminate {
          width: 100%;
          will-change: transform;
          /* stylelint-disable-next-line max-line-length */
          animation: indeterminate 1.5s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite;
        }

        .indeterminate:-moz-dir(rtl) {
          /* stylelint-disable-next-line max-line-length */
          animation: indeterminate-rtl 1.5s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite;
        }

        @keyframes indeterminate {
          0% {
            transform: translateX(-100%);
          }

          100% {
            transform: translateX(100%);
          }
        }

        @keyframes indeterminate-rtl {
          0% {
            transform: translateX(100%);
          }

          100% {
            transform: translateX(-100%);
          }
        }

        :-moz-dir(rtl) .subtitle-2,
        .progress:-moz-dir(rtl) {
          text-align: right;
        }
      `,
      /* eslint-enable */
    ];
  }

  constructor() {
    super();
    this.percent = 0;
    this.bufferedpercent = 0;
    this.type = types.DETERMINED;
    this.label = '';
  }

  firstUpdated() {
    super.firstUpdated();
    if (this.percent > 100) {
      this.percent = 100;
    } else if (this.percent < 0) {
      this.percent = 0;
    }
    if (this.type === types.BUFFERED) {
      if (this.bufferedpercent > 100) {
        this.bufferedpercent = 100;
      } else if (this.bufferedpercent < 0) {
        this.bufferedpercent = 0;
      }
    }
  }

  get wrapper() {
    return this.shadowRoot.querySelector('.wrapper');
  }

  get indicator() {
    return this.shadowRoot.querySelector('.indicator');
  }

  get status() {
    return this.shadowRoot.querySelector('.status');
  }

  getXPercentage(percent) {
    return (this.isRtl ? 100 - percent : percent - 100) + '%';
  }

  updated() {
    if (this.indicator) {
      switch (this.type) {
        case types.DETERMINED:
          this.indicator.style.transform = `
            translateX(${this.getXPercentage(this.percent)})
          `;
          break;
        case types.ONTOP:
          this.indicator.style.transform = `
            translateX(${this.getXPercentage(this.percent)})
          `;
          this.wrapper.classList.toggle('complete', this.percent === 100);
          this.indicator.classList.toggle('complete', this.percent === 100);
          break;
        case types.BUFFERED:
          {
            this.indicator.style.transform = `
              translateX(${this.getXPercentage(this.bufferedpercent)})
            `;
            this.status.style.transform = `
              translateX(${this.getXPercentage(this.percent)})
            `;
          }
          break;
        default:
          break;
      }
    }
  }

  render() {
    return html`
      <div role="progressbar" aria-valuetext="${this.label}">
        ${this.label
          ? html`
              <div
                class="subtitle-2 label"
                dir="auto"
              >
                ${this.label}
              </div>
            `
          : ''}
        <div
          class="
            ${this.type === types.ONTOP ? '' : 'space'}
            progress
          "
        >
          <div class="wrapper ${this.type === types.ONTOP ? 'ontop' : ''}">
            <div
              class=${this.type === types.ONTOP ? 'ontop-track' : 'track'}
            ></div>
            <div class="indicator ${this.type}"></div>
            ${this.type === types.BUFFERED
              ? html`
                  <div class="status"></div>
                `
              : ''}
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define(ProgressElement.is, ProgressElement);
